import { TextArea } from '@blueprintjs/core';
import OrganizationSelect from 'Components/Generic/BOSFilterSelect/OrganizationSelect';
import UserSelect from 'Components/Generic/BOSFilterSelect/UserSelect';
import VendorSelect from 'Components/Generic/BOSFilterSelect/VendorSelect';
import AddressSelect from 'Components/Generic/BOSSelect/AddressSelect';
import ButtonFooter from 'Components/Generic/Buttons/ButtonFooter';
import CancelButton from 'Components/Generic/Buttons/CancelButton';
import SaveButton from 'Components/Generic/Buttons/SaveButton';
import UndoButton from 'Components/Generic/Buttons/UndoButton';
import DetailTable from 'Components/Generic/DetailPanel/DetailTable';
import AppData from 'Data/AppData';
import { getUserOrgIds } from 'Data/context/Organizations';
import { useShippingContext } from 'Data/context/Shipping';
import { user_org_roles, user_roles } from 'Data/Enum';
import GenericTableData from 'Data/GenericTableData';
import RequestData from 'Data/RequestData';
import DataManager from 'Lib/DataManager';
import { useUndoableSaveAction } from 'Lib/hooks/useUndoableSaveAction';
import { useUpdatableObject } from 'Lib/hooks/useUpdatableObject';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { isUserAuthorized } from 'Util';
import { getTagValue, setTagValue } from 'Util/tagUtil';

let DM = new DataManager();
const selectWidth = 200;
let OrgAddressData = GenericTableData.getTableDataBus(
  'ORG_ADD',
  'organization_addresses',
);
let UserAddressData = GenericTableData.getTableDataBus(
  'USER_ADD',
  'user_addresses',
);

export const useEditableRequestInformation = ({
  requestObject,
  isNew = false,
  setEditState = () => {},
}) => {
  const {
    currentObject: request,
    hasBeenUpdated,
    reset: resetRequest,
    updateField,
    originalObject: originalRequest,
    doSave,
  } = useUpdatableObject(RequestData.updateRecord, RequestData.createRequest, [
    'parent_organization_id',
  ]);
  const [resetUndo, showSaveUndoToast] = useUndoableSaveAction({
    originalObject: originalRequest,
    undoFunction: RequestData.updateRecord,
    onUndo: (originalRequest) => fetchRequest(originalRequest.id),
    objectType: 'Request',
  });

  const [userAddressIdList, setUserAddressIdList] = useState([]);
  const [orgAddressIdList, setOrgAddressIdList] = useState([]);

  const shippingContext = useShippingContext();
  const navigate = useNavigate();

  useEffect(() => {
    DM.add(
      RequestData.registerListener('REQUEST_LOADED', onRequestLoaded),
      RequestData,
    );
    DM.add(
      RequestData.registerListener('RECORD_UPDATED', onRequestUpdated),
      RequestData,
    );
    DM.add(
      OrgAddressData.registerListener('LIST_LOADED', onOrgAddressesLoaded),
      OrgAddressData,
    );
    DM.add(
      UserAddressData.registerListener('LIST_LOADED', onUserAddressesLoaded),
      UserAddressData,
    );
    return () => {
      DM.clear();
    };
  }, []);

  useEffect(() => {
    setEditState(undefined);
  }, []);

  useEffect(() => {
    resetRequestTo(requestObject);
  }, [requestObject]);

  const onRequestLoaded = (payload) => {
    if (payload) {
      resetRequestTo(payload);
    }
  };

  const resetRequestTo = (updatedRequestObject) => {
    const {
      id,
      parent_organization_id,
      requesting_user_id,
      requested_delivery_address_id,
      vendor_id,
      processing_user_id,
      notes,
      status,
      shipping_speed,
      line_items,
      tags,
    } = updatedRequestObject;
    resetRequest({
      id,
      parent_organization_id,
      requesting_user_id,
      requested_delivery_address_id,
      vendor_id,
      processing_user_id,
      notes,
      status,
      shipping_speed,
      line_items,
      tags,
    });
    setOrgAddressIdList([]);
    setUserAddressIdList([]);
    resetUndo(() => fetchRequest(updatedRequestObject.id));
  };

  const fetchRequest = (id) => {
    RequestData.getRequest(id);
  };

  const onRequestUpdated = (payload) => {
    if (payload) {
      showSaveUndoToast();
      setEditState(false);
    }
  };

  useEffect(() => {
    if (request.requesting_user_id) {
      UserAddressData.getRecordList({
        where: { '*user_id': request.requesting_user_id },
      });
    }
  }, [request.requesting_user_id]);

  const onUserAddressesLoaded = (payload) => {
    if (payload) {
      setUserAddressIdList(payload.records.map((record) => record.address_id));
    }
  };

  useEffect(() => {
    if (request.parent_organization_id) {
      OrgAddressData.getRecordList({
        where: { '*organization_id': request.parent_organization_id },
      });
    }
  }, [request.parent_organization_id]);

  const onOrgAddressesLoaded = (payload) => {
    if (payload) {
      setOrgAddressIdList(payload.records.map((record) => record.address_id));
    }
  };

  let addressIdList = [
    ...(originalRequest?.requested_delivery_address_id
      ? [originalRequest.requested_delivery_address_id]
      : []),
    ...userAddressIdList.filter(
      (addId) => addId !== originalRequest?.requested_delivery_address_id,
    ),
    ...orgAddressIdList.filter(
      (addId) => addId !== originalRequest?.requested_delivery_address_id,
    ),
  ];

  useEffect(() => {
    if (
      request?.requested_delivery_address_id &&
      !addressIdList.includes(request.requested_delivery_address_id)
    ) {
      updateField('requested_delivery_address_id', null);
    }
  }, [userAddressIdList, orgAddressIdList]);

  const updateDeliveryAddress = (add) => {
    updateField('requested_delivery_address_id', add?.id || null);
    shippingContext?.shippingItem &&
      shippingContext.updateShipping('delivery_address_id', add?.id || null);
  };

  let detailData = [
    {
      label: 'Core Facility',
      value: (
        <OrganizationSelect
          key={request.parent_organization_id}
          defaultSelectedId={request.parent_organization_id}
          onSelectOrganization={(org) =>
            updateField('parent_organization_id', org?.id || null)
          }
          readOnly={
            !isUserAuthorized({
              roles: [
                user_roles.ADMINISTRATOR,
                user_roles.ADMINISTRATIVE_APPROVER,
                user_roles.BUYER,
                user_roles.TRANSACTOR,
              ],
            }) && getUserOrgIds()?.length === 1
          }
          isRequired
        />
      ),
    },
    {
      label: 'Requester',
      value: (
        <div style={{ minWidth: selectWidth }}>
          <UserSelect
            isRequired
            readOnly={
              !isUserAuthorized({
                roles: [
                  user_roles.ADMINISTRATOR,
                  user_roles.ADMINISTRATIVE_APPROVER,
                  user_roles.BUYER,
                  user_roles.TRANSACTOR,
                  ['*', user_org_roles.MANAGER],
                ],
              })
            }
            defaultSelectedId={request.requesting_user_id}
            onSelectUser={(user) =>
              updateField('requesting_user_id', user?.id || null)
            }
            orgIds={request?.parent_organization_id || getUserOrgIds()}
            includedId={originalRequest.requesting_user_id}
          />
        </div>
      ),
    },
    {
      label: 'Delivery Address',
      value: (
        <AddressSelect
          required
          defaultSelectedId={request.requested_delivery_address_id}
          addressIdList={addressIdList}
          onSelectAddress={updateDeliveryAddress}
          labelText={
            !addressIdList?.length
              ? !request?.parent_organization_id
                ? 'Select a Core Facility first...'
                : !request?.requesting_user_id
                ? 'Select a Requester first...'
                : 'No addresses for selected core and/or user...'
              : ''
          }
        />
      ),
    },
    {
      label: 'Vendor',
      value: (
        <VendorSelect
          key={request.vendor_id}
          defaultSelectedId={request.vendor_id}
          onSelectVendor={(vendor) =>
            updateField('vendor_id', vendor?.id || null)
          }
          isRequired
          isForRequest
        />
      ),
    },
    ...(isUserAuthorized({
      roles: [
        user_roles.BUYER,
        user_roles.TRANSACTOR,
        user_roles.ADMINISTRATOR,
      ],
    })
      ? [
          {
            label: 'Processor',
            value: (
              <UserSelect
                defaultSelectedId={
                  request.processing_user_id ||
                  (isNew && AppData.userID()) ||
                  null
                }
                onSelectUser={(user) =>
                  updateField('processing_user_id', user?.id || null)
                }
                userRoles={[user_roles.BUYER.key, user_roles.TRANSACTOR.key]}
                includedId={originalRequest.processing_user_id}
              />
            ),
          },
        ]
      : []),
    {
      label: 'Description',
      value: (
        <TextArea
          fill
          autoResize
          value={request.notes}
          onChange={(e) => {
            updateField('notes', e.target.value);
          }}
        />
      ),
    },
    {
      label: (
        <span style={{ color: 'var(--site-warning-color)' }}>Radioactive</span>
      ),
      value: (
        <div style={{ display: 'flex' }}>
          <input
            type='checkbox'
            checked={getTagValue(request, 'is_radioactive')}
            onChange={(event) => {
              updateField(
                'tags',
                setTagValue(request, 'is_radioactive', event.target.checked),
              );
            }}
          />
        </div>
      ),
    },
  ];

  const saveClicked = () => {
    if (
      shippingContext?.shippingItem?.id &&
      Object.hasOwn(shippingContext.updatedFields, 'delivery_address_id')
    ) {
      shippingContext.saveShipping().then(() => {
        hasBeenUpdated && doSave();
        !hasBeenUpdated && setEditState(false);
      });
    } else {
      doSave();
    }
  };

  const requiredFieldsPresent =
    request?.parent_organization_id &&
    request?.requesting_user_id &&
    request?.requested_delivery_address_id &&
    request?.vendor_id;

  const saveButton = hasBeenUpdated && (
    <SaveButton clickHandler={saveClicked} disabled={!requiredFieldsPresent} />
  );
  let undoClickHandler = () => {
    fetchRequest(originalRequest.id);
  };

  const undoButton = !isNew
    ? hasBeenUpdated && <UndoButton clickHandler={undoClickHandler} />
    : !request.id && (
        <CancelButton fill={false} onCancel={() => navigate(-1)} />
      );

  const footer = (
    <ButtonFooter>
      {undoButton}
      {saveButton}
    </ButtonFooter>
  );

  const editableTable = <DetailTable data={detailData} editable />;

  return { editableTable, footer, request };
};
